/**
 * frontend/src/pages/ClaimPage.jsx — /claim/:artistId (public)
 * SoundDAO Sprint 6A Prompt 6 — the page a band lands on from a fan's nomination.
 * Claimed already → redirect to the creator profile.
 *
 * Load order (pre-outreach): the core call is one DB read (name, Archive link, stats, CTA) and
 * renders as soon as it lands; playlists and tracks come from /activity afterwards and never
 * block first paint. Nothing on this page calls archive.org.
 */

import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { claimActivity, claimPage, formatDuration, setPendingClaim } from '../lib/archiveApi';
import { ARTIST_FIRST_CLAIM, ARTIST_PITCH, artistHeadline } from '../lib/artistCopy';
import FounderPickTag from '../components/archive/FounderPickTag';
import { usePlayer } from '../player/PlayerContext';
import { track } from '../lib/analytics';
import '../LandingPage.css';
import '../styles/archive.css';
import BrandMark from '../components/BrandMark';

export default function ClaimPage({ session, user }) {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { playQueue, isCurrent, toggle, status } = usePlayer();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let alive = true;
    // Both requests start together; the page renders from the core one alone.
    claimPage(artistId).then((d) => { if (!alive) return; setData(d); track('claim_page_viewed'); }).catch((e) => alive && setError(e));
    claimActivity(artistId).then((d) => alive && setActivity(d)).catch(() => alive && setActivity({ playlists: [], topTracks: [] }));
    return () => { alive = false; };
  }, [artistId]);

  useEffect(() => {
    document.title = data?.artist ? `${data.artist.displayName} — claim your page on SoundDAO` : 'Claim your page — SoundDAO';
    return () => { document.title = 'SoundDAO'; };
  }, [data]);

  if (data?.artist?.status === 'claimed' && data.artist.creatorUserId) {
    return <Navigate to={`/creator/${data.artist.creatorUserId}`} replace />;
  }

  const a = data?.artist;
  const topTracks = activity?.topTracks || [];
  const playlists = activity?.playlists || [];
  const stats = data?.stats || { playlistCount: a?.playlistCount || 0, nominatorCount: a?.nominatorCount || 0, archiveShows: 0 };
  const statLines = [
    stats.playlistCount > 0 && <>Featured in <b>{stats.playlistCount}</b> SoundDAO playlist{stats.playlistCount === 1 ? '' : 's'}</>,
    stats.nominatorCount > 0 && <><b>{stats.nominatorCount}</b> fan{stats.nominatorCount === 1 ? '' : 's'} nominated you</>,
    stats.archiveShows > 0 && <>Recordings on the Archive: <b>{stats.archiveShows}</b> show{stats.archiveShows === 1 ? '' : 's'}{stats.archiveSpan ? ` (${stats.archiveSpan})` : ''}</>,
  ].filter(Boolean);
  const queue = topTracks.map((t) => ({
    key: `${t.identifier}/${t.fileName}`, title: t.title, artist: t.artistDisplay || a?.displayName || '',
    streamUrl: t.streamUrl, archiveUrl: t.archiveUrl, identifier: t.identifier, from: 'claim_page',
  }));

  const claim = () => {
    track('claim_started');
    setPendingClaim(artistId);
    if (!session) navigate('/login');
    else if (!user?.persona) navigate('/onboarding');
    else navigate(`/claim/${artistId}/verify`);
  };

  return (
    <div style={{ background: '#12112b', minHeight: '100vh' }}>
      {!session && (
        <header className="ax-pubhead">
          <Link to="/" className="ax-logo"><BrandMark /> SoundDAO</Link>
          <Link to="/login" className="ax-btn ax-btn--sm">Sign in</Link>
        </header>
      )}
      <div className="ax-page" style={{ minHeight: 0 }}>
        <div className="ax-wrap">
          {error && <div className="ax-empty">{error.status === 404 ? 'This artist isn’t on SoundDAO’s Archive list.' : error.message}</div>}
          {!data && !error && <><div className="ax-skel" style={{ height: 140 }} /><div className="ax-skel" /></>}
          {a && (
            <div className="ax-two">
              <div>
                <div className="ax-meta">ON THE INTERNET ARCHIVE{a.archiveUrl && <> · <a href={a.archiveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc' }}>archive.org ↗</a></>}{a.founderPick && <> <FounderPickTag /></>}</div>
                <h1 className="ax-h1" style={{ fontSize: 28 }}>{a.displayName}</h1>
                {/* A stat is shown only when it is non-zero ("0 playlists" reads as a put-down). */}
                {statLines.length > 0 ? (
                  <ul className="ax-sub claim-stats" style={{ fontSize: 15, color: '#fff', listStyle: 'none', padding: 0 }}>
                    {statLines.map((l, i) => <li key={i} style={{ margin: '4px 0' }}>{l}</li>)}
                  </ul>
                ) : (
                  <p className="ax-sub claim-first" style={{ fontSize: 15, color: '#fff' }}>{ARTIST_FIRST_CLAIM}</p>
                )}

                {playlists.length > 0 && (
                  <>
                    <h2 className="ax-h2">On fans&#39; playlists</h2>
                    <ul className="claim-playlists" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {playlists.map((pl) => (
                        <li key={pl.slug} className="ax-track">
                          <div className="ax-grow">
                            <Link to={`/playlist/${pl.slug}`} className="ax-title" style={{ color: '#fff', textDecoration: 'none' }}>{pl.title}</Link>
                            <div className="ax-meta">by {pl.ownerName}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {a.status === 'claim_pending' && <div className="ax-banner">Someone has started claiming this profile. If that’s you, sign in to finish verifying.</div>}

                {topTracks.length > 0 && (
                  <>
                    <h2 className="ax-h2">Most-added tracks</h2>
                    {topTracks.map((t, i) => {
                      const on = isCurrent(queue[i].key);
                      const playing = on && (status === 'playing' || status === 'loading');
                      return (
                        <div className="ax-track" key={queue[i].key}>
                          <button type="button" className={`ax-play ${on ? 'on' : ''}`} onClick={() => (on ? toggle() : playQueue(queue, i))} aria-label={playing ? `Pause ${t.title}` : `Play ${t.title}`}>
                            {playing ? '❚❚' : '▶'}
                          </button>
                          <div className="ax-grow">
                            <div className="ax-title">{t.title}</div>
                            <div className="ax-meta">{t.addedTo > 0 && <>on {t.addedTo} playlist{t.addedTo === 1 ? '' : 's'} · </>}<a href={t.archiveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc', textDecoration: 'none' }}>archive.org ↗</a></div>
                          </div>
                          <span className="ax-dur">{formatDuration(t.durationSec)}</span>
                        </div>
                      );
                    })}
                    <p className="ax-attr">Streaming from the Internet Archive</p>
                  </>
                )}
              </div>

              <div>
                <div className="ax-cta">
                  <h2 className="vp-headline" style={{ fontSize: 22, textAlign: 'left', margin: 0 }}>
                    {artistHeadline(stats)}
                  </h2>
                  <p className="vp-sub" style={{ textAlign: 'left', margin: '10px 0 14px' }}>
                    {ARTIST_PITCH}
                  </p>
                  {!data.hasCheckableLinks && (
                    <div className="ax-banner" style={{ marginTop: 0 }}>
                      We couldn&#39;t find an official website or Bandcamp page for you yet. You can still join as a creator now.
                    </div>
                  )}
                  {data.hasCheckableLinks && (
                    <p className="ax-meta" style={{ marginBottom: 12 }}>
                      Verification is automatic: sign in with an email at your official domain, or put a short code on your
                      {data.methods.includes('page_code') ? ' official website or Bandcamp page' : ' website'}.
                    </p>
                  )}
                  <button type="button" className="ax-btn ax-btn--primary" onClick={claim}>Claim your profile</button>
                  <p className="ax-meta" style={{ marginTop: 10 }}>Free. Takes a couple of minutes.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
